/* === MODERN COLOR UTILITIES FOR MERCHANT TABLE === */

/**
 * Modern, balanced base colors
 * (green → yellow → red) gradient
 */
const MINT_GREEN = { r: 72, g: 187, b: 120 };   // soft green for healthy metrics
const GOLDEN_YELLOW = { r: 255, g: 221, b: 87 }; // neutral midpoint
const WARM_RED = { r: 239, g: 71, b: 111 };      // warm coral red for poor metrics

/**
 * Linear interpolation between two RGB colors
 */
export function interpolateColor(color1: any, color2: any, ratio: number) {
  const r = Math.round(color1.r + (color2.r - color1.r) * ratio);
  const g = Math.round(color1.g + (color2.g - color1.g) * ratio);
  const b = Math.round(color1.b + (color2.b - color1.b) * ratio);
  return `rgb(${r},${g},${b})`;
}

/**
 * Optional: desaturate a color slightly for softer appearance
 * (helps avoid neon tones on light UIs)
 */
export function desaturate(rgbString: string, factor = 0.15) {
  const [r, g, b] = rgbString.match(/\d+/g)!.map(Number);
  const avg = (r + g + b) / 3;
  return `rgb(${Math.round(r + (avg - r) * factor)},
              ${Math.round(g + (avg - g) * factor)},
              ${Math.round(b + (avg - b) * factor)})`;
}

/**
 * Color gradient for cancellation rate
 * 0%   = green (good)
 * 50%  = yellow (warning)
 * 100% = red (bad)
 */
export const getCancellationColor = (rate: number) => {
  const ratio = Math.min(Math.max(rate / 100, 0), 1);
  let color: string;

  if (ratio < 0.5) {
    // 0–50%: green → yellow
    color = interpolateColor(MINT_GREEN, GOLDEN_YELLOW, ratio / 0.5);
  } else {
    // 50–100%: yellow → red
    color = interpolateColor(GOLDEN_YELLOW, WARM_RED, (ratio - 0.5) / 0.5);
  }

  return desaturate(color, 0.1);
};

/**
 * Text display for "time ago"
 */
export const parseTimeAgoToDaysOrHours = (seconds: number): string => {
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  } else if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(seconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
};

/**
 * Color gradient for last order time
 * 0 days   = green (recent)
 * 15 days  = yellow (stale)
 * 30+ days = red (inactive)
 */
export const getLastOrderColorFromSeconds = (seconds: number): string => {
  const days = Math.min(seconds / 86400, 30); // cap at 30 days
  const ratio = days / 30;
  let color: string;

  if (ratio < 0.5) {
    // 0–15 days: green → yellow
    color = interpolateColor(MINT_GREEN, GOLDEN_YELLOW, ratio / 0.5);
  } else {
    // 15–30 days: yellow → red
    color = interpolateColor(GOLDEN_YELLOW, WARM_RED, (ratio - 0.5) / 0.5);
  }

  return desaturate(color, 0.1);
};
